import type { WorkspaceTheme } from '../types';

/** token do tema → custom property usada no CSS */
const TOKEN_TO_VAR: Record<keyof WorkspaceTheme, string> = {
  primary: '--crm-primary',
  primaryForeground: '--crm-primary-foreground',
  accent: '--crm-accent',
  sidebar: '--crm-sidebar',
  sidebarForeground: '--crm-sidebar-foreground',
  fontFamily: '--crm-font',
};

/** Valores de fábrica (iguais ao index.css) — "Restaurar padrão" volta para eles. */
export const DEFAULT_THEME: Required<WorkspaceTheme> = {
  primary: '#7C3AED',
  primaryForeground: '#FFFFFF',
  accent: '#22D3EE',
  sidebar: '#111827',
  sidebarForeground: '#E5E7EB',
  fontFamily: 'Inter',
};

/** Fontes da Aparência (Google Fonts). A mesma lista é validada no servidor. */
export const FONT_OPTIONS = [
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Poppins',
  'Nunito',
  'DM Sans',
  'Source Sans 3',
  'Merriweather',
] as const;

/** "Poppins" → pilha CSS com reservas; valores antigos ("Inter, sans-serif") passam como vieram. */
export const fontStack = (font: string): string =>
  font.includes(',') ? font : `'${font}', system-ui, -apple-system, 'Segoe UI', sans-serif`;

/** Carrega a fonte do Google Fonts uma vez (Inter já vem no index.html). */
export function loadFont(font: string | undefined) {
  if (!font || font.includes(',') || !(FONT_OPTIONS as readonly string[]).includes(font))
    return;
  const id = `crm-font-${font.replace(/\s+/g, '-').toLowerCase()}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font).replace(/%20/g, '+')}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

/** Variáveis CSS de um tema — para aplicar na raiz ou numa prévia local. */
export function themeVars(theme: WorkspaceTheme | undefined): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const [token, cssVar] of Object.entries(TOKEN_TO_VAR)) {
    const value = theme?.[token as keyof WorkspaceTheme];
    if (!value) continue;
    vars[cssVar] = token === 'fontFamily' ? fontStack(value) : value;
  }
  return vars;
}

/**
 * Aplica o tema do workspace na raiz do documento. É o único ponto onde os
 * projetos (Vânia, Dantas, Marco, Fernando Dantas) diferem visualmente.
 * Token ausente volta ao valor de fábrica do index.css.
 */
export const applyTheme = (theme: WorkspaceTheme | undefined): void => {
  const root = document.documentElement;
  const vars = themeVars(theme);
  for (const cssVar of Object.values(TOKEN_TO_VAR)) {
    if (vars[cssVar]) root.style.setProperty(cssVar, vars[cssVar]);
    else root.style.removeProperty(cssVar);
  }
  loadFont(theme?.fontFamily);
};
